import React, { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import {
    BarChartOutlined,
    FundOutlined, ReconciliationOutlined,
    LoginOutlined, MenuOutlined, MessageOutlined, PieChartOutlined,
    SearchOutlined, TeamOutlined, TruckOutlined, WhatsAppOutlined, WalletOutlined,
    UserSwitchOutlined
} from '@ant-design/icons';
import { ApprovalCenter } from './approvals/ApprovalCenter';
import SettingsModal from './SettingsModal/SettingsModal';
import { Avatar, Badge, Button, ConfigProvider, Dropdown, Layout, Menu, Space, Spin, theme, Tooltip as AntTooltip } from 'antd';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from './contexts/AuthContext'; // Updated import
import { useWatiChat } from './contexts/WatiChatContext';
import { useWatiSocket } from './contexts/WatiSocketContext';
import { useSocket } from './contexts/SocketContext';
import { API_DOMAIN } from './constants';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import useSnackbar from "../components/Snackbar/UseSnackbar";
import useLogout from './Login/Logout';
import siteConfig from '../siteConfig';
import uzdLogo from '../assets/images/uzd_logo_small.jpeg';

const { Header, Content, Footer, Sider } = Layout;

// Brand block at the top of the sider — logo on a white plate (artwork has a
// white background), wordmark hidden when collapsed
const SiderBrand = ({ collapsed }) => (
    <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: collapsed ? "16px 0" : "16px 16px",
        justifyContent: collapsed ? "center" : "flex-start",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        marginBottom: 8,
    }}>
        <div style={{
            background: "#fff", borderRadius: 8, padding: "4px 6px", flexShrink: 0,
            boxShadow: "0 2px 10px rgba(225,29,46,0.3)",
        }}>
            <img src={uzdLogo} alt="UZD" style={{ height: 22, display: "block", objectFit: "contain" }} />
        </div>
        {!collapsed && (
            <div style={{ lineHeight: 1.15 }}>
                <div style={{ color: "#f1f5f9", fontWeight: 800, fontSize: 13, letterSpacing: 1.5 }}>UZDPART</div>
                <div style={{ color: "#e11d2e", fontWeight: 700, fontSize: 10, letterSpacing: 3 }}>SRM</div>
            </div>
        )}
    </div>
);

function getItem(label, key, icon, path) {
    return { key, icon, label: <Link to={path}>{label}</Link>, path, title: label };
}

if (!document.getElementById("drawer-credit-style")) {
    const s = document.createElement("style");
    s.id = "drawer-credit-style";
    s.textContent = `
        @keyframes credit-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes credit-pulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
    `;
    document.head.appendChild(s);
}

const DrawerNew = () => {
    const { handledialogueOpen, LogoutDialog } = useLogout();
    const { SuccessSnackbar, ErrorSnackbar, InfoSnackbar, WarningSnackbar, ToastContainer } = useSnackbar();

    // Use the new AuthContext hook
    const { authState, legacyAuthState } = useAuth();
    const { totalUnread, qv4TotalUnread } = useWatiChat();
    const { isWatiConnected } = useWatiSocket();
    const { watiPresence } = useSocket();

    const [templateCredit, setTemplateCredit] = useState(null);
    const [creditLoading, setCreditLoading] = useState(false);
    const [globalSettingsOpen, setGlobalSettingsOpen] = useState(false);
    const isAdmin = !!authState?.user?.admin;
    const [pageDrawerWidth, setPageDrawerWidth] = useState(0);

    const fetchCredit = () => {
        setCreditLoading(true);
        axios.get(API_DOMAIN + "/wati_messages/get_credits_status", {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then(res => {
            const payload = res.data?.data ?? res.data;
            const val = payload?.creditCustomer?.credit ?? null;
            setTemplateCredit(val);
        }).catch(() => {}).finally(() => setCreditLoading(false));
    };

    useEffect(() => {
        fetchCredit();
        const interval = setInterval(fetchCredit, 2 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);
    const location = useLocation();
    const navigate = useNavigate();
    const isQV4 = location.pathname === "/" || location.pathname === "/querysetv4" || location.pathname === "/task_management" || location.pathname === "/whatsapp_messages";

    // For backward compatibility, use legacyAuthState if available
    const userState = legacyAuthState || authState;

    function stringAvatar(name) {
        if (!name) return "UU";
        if (name.split(' ').length === 2) {
            return `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`;
        } else {
            return `${name.split(' ')[0][0].toUpperCase()}`;
        }
    }

    // Define menu items - use isAuthenticated from new authState
    const items = authState.isAuthenticated
        ? [
            getItem('Dashboard', '/dashboard', <PieChartOutlined />, '/dashboard'),
            getItem('Queryset V3', '/queryset', <WhatsAppOutlined />, '/queryset'),
            getItem('Sales Pipeline', '/querysetv4', <Badge count={qv4TotalUnread} size="small" offset={[10, 10]}><FundOutlined /></Badge>, '/querysetv4'),
            getItem('Operations', '/task_management', <ReconciliationOutlined />, '/task_management'),
            getItem('WhatsApp Messages', '/whatsapp_messages', <Badge count={totalUnread} size="small" offset={[10, 10]}><MessageOutlined /></Badge>, '/whatsapp_messages'),
            getItem('SRM', '/suppliers', <TruckOutlined />, '/suppliers'),
            getItem('HRM', '/hrm', <TeamOutlined />, '/hrm'),
            getItem('Reports', '/reports', <BarChartOutlined />, '/reports'),
            getItem('Search Results', '/search_results', <SearchOutlined />, '/search_results'),
        ]
        : [];

    const isMobile = useIsMobile();
    const [collapsed, setCollapsed] = useState(() => window.innerWidth < 768);
    const [selectedKey, setSelectedKey] = useState('');
    const [currentHeader, setCurrentHeader] = useState('');
    const [isLoading, setIsLoading] = useState(false); // Start as false since auth is handled by App

    // Auto-collapse on desktop after 2.5s; mobile starts collapsed already
    useEffect(() => {
        if (isMobile) return;
        const t = setTimeout(() => setCollapsed(true), 2500);
        return () => clearTimeout(t);
    }, [isMobile]);

    // Set menu and header based on current location
    useEffect(() => {
        if (!authState.isAuthenticated) return;

        const setInitialMenuState = () => {
            // Find exact match first
            let matchedItem = items.find(item =>
                location.pathname === item.path
            );

            // If no exact match, try to match path prefix
            if (!matchedItem) {
                matchedItem = items.find(item =>
                    location.pathname.startsWith(item.path)
                );
            }

            // Special case for reports
            if (!matchedItem && location.pathname.startsWith('/report')) {
                matchedItem = items.find(item => item.path === '/reports');
            }

            if (matchedItem) {
                setSelectedKey(matchedItem.key);
                setCurrentHeader(matchedItem.title);
            } else {
                setSelectedKey('');
                setCurrentHeader('Welcome');
            }
        };

        setInitialMenuState();
    }, [location.pathname, items, authState.isAuthenticated]);

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Don't show loading spinner here - auth is handled by App
    if (!authState.isAuthenticated) {
        return null; // Or a redirect component
    }

    const dropdown_items = [
        {
            key: '5',
            label: "User Settings",
        },
        ...(isAdmin ? [{
            key: '4',
            label: "Global Settings",
        }] : []),
        {
            key: '3',
            label: "Logout",
        },
    ];

    const onMenuClick = (e) => {
        switch (e.key) {
            case "5":
                InfoSnackbar(`Keep calm, coffee is being converted to code...`);
                break;
            case "3":
                handledialogueOpen();
                break;
            case "4":
                setGlobalSettingsOpen(true);
                break;
            default:
                ErrorSnackbar(`Invalid option selected!`);
                break;
        }
    };

    return (
        <ConfigProvider theme={{
            algorithm: theme.defaultAlgorithm,
            token: {
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Open Sans', 'Helvetica Neue', Arial, sans-serif",
                borderRadius: 8,
                colorBgLayout: "#f4f5f7",
                colorBorderSecondary: "#eceef1",
                boxShadowSecondary: "0 4px 16px rgba(15, 23, 42, 0.08), 0 1px 4px rgba(15, 23, 42, 0.05)",
            },
            components: {
                Layout: {
                    siderBg: "#0e1013",
                    triggerBg: "#16181d",
                    triggerColor: "rgba(255,255,255,0.55)",
                },
                Button: { fontWeight: 500 },
                Card: { borderRadiusLG: 12 },
                Modal: { borderRadiusLG: 14 },
                Drawer: { borderRadiusLG: 14 },
                Table: { headerBg: "#f7f8fa", headerSplitColor: "transparent" },
                Tag: { borderRadiusSM: 6 },
                Collapse: { headerBg: "#fafbfc", borderRadiusLG: 10 },
                Notification: { borderRadiusLG: 12 },
                Menu: {
                    darkItemBg: "#0e1013",
                    darkSubMenuItemBg: "#0e1013",
                    darkPopupBg: "#16181d",
                    darkItemColor: "rgba(255,255,255,0.62)",
                    darkItemHoverBg: "rgba(255,255,255,0.07)",
                    darkItemHoverColor: "#ffffff",
                    darkItemSelectedBg: "#e11d2e",
                    darkItemSelectedColor: "#ffffff",
                    itemBorderRadius: 8,
                    itemMarginInline: 8,
                    itemHeight: 42,
                },
            },
        }}>
            <Layout style={isQV4 ? { height: '100vh', width: '100vw', overflow: 'hidden' } : { minHeight: '100vh', minWidth: '100vw' }}>

                {/* ── Desktop: sticky collapsible sider ── */}
                {!isMobile && (
                    <Sider
                        collapsible
                        collapsed={collapsed}
                        onCollapse={setCollapsed}
                        style={{ overflow: "auto", height: "100vh", position: "sticky", top: 0, left: 0 }}
                    >
                        <SiderBrand collapsed={collapsed} />
                        <Menu theme="dark" selectedKeys={[selectedKey]} defaultOpenKeys={['pipeline-group']} mode="inline" items={items} />
                    </Sider>
                )}

                {/* ── Mobile: full-screen overlay when open ── */}
                {isMobile && !collapsed && (
                    <>
                        <div
                            onClick={() => setCollapsed(true)}
                            style={{
                                position: "fixed", inset: 0,
                                background: "rgba(0,0,0,0.5)",
                                zIndex: 1000,
                            }}
                        />
                        <Sider
                            width={230}
                            style={{
                                position: "fixed", top: 0, left: 0,
                                height: "100dvh", zIndex: 1001,
                                overflow: "auto",
                            }}
                        >
                            <SiderBrand collapsed={false} />
                            <Menu
                                theme="dark"
                                selectedKeys={[selectedKey]}
                                                               mode="inline"
                                items={items}
                                onClick={() => setCollapsed(true)}
                            />
                        </Sider>
                    </>
                )}

                <Layout style={isQV4 ? { flex: 1, overflow: 'hidden', minHeight: 0 } : {}}>
                    <div className='sticky top-0 left-0 z-99'>
                        <Header
                            style={{
                                padding: isMobile ? "0 10px" : "0 20px",
                                width: "100%",
                                height: 56,
                                lineHeight: "56px",
                                background: "#0e1013",
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: isQV4 ? 0 : 12,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                {isMobile && (
                                    <Button
                                        type="text"
                                        icon={<MenuOutlined style={{ fontSize: 18 }} />}
                                        onClick={() => setCollapsed(false)}
                                        style={{ color: "white" }}
                                    />
                                )}
                                <span style={{
                                    width: 8, height: 8, borderRadius: 2, background: "#e11d2e",
                                    display: "inline-block", flexShrink: 0, transform: "rotate(45deg)",
                                }} />
                                <h1 style={{
                                    margin: 0, fontSize: isMobile ? 15 : 17, fontWeight: 700,
                                    letterSpacing: 0.3, whiteSpace: "nowrap", overflow: "hidden",
                                    textOverflow: "ellipsis", color: "#f1f5f9",
                                }}>
                                    {currentHeader}
                                </h1>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginRight: pageDrawerWidth, transition: "margin-right 0.15s" }}>
                                {templateCredit !== null && (() => {
                                    const creditColor = templateCredit >= 10 ? "#52c41a" : templateCredit >= 5 ? "#faad14" : "#ff4d4f";
                                    const creditBg   = templateCredit >= 10 ? "rgba(82,196,26,0.12)" : templateCredit >= 5 ? "rgba(250,173,20,0.12)" : "rgba(255,77,79,0.12)";
                                    return (
                                        <AntTooltip title="Template credit balance" placement="bottom">
                                            <div
                                            onClick={fetchCredit}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 5,
                                                cursor: creditLoading ? "wait" : "pointer",
                                                backgroundColor: creditBg,
                                                border: `1.5px solid ${creditColor}`,
                                                borderRadius: 8,
                                                alignSelf: "stretch",
                                                padding: "0 12px 0 10px",
                                                opacity: creditLoading ? 0.6 : 1,
                                                animation: creditLoading ? "credit-pulse 0.8s ease-in-out infinite" : "none",
                                                transition: "opacity 0.2s",
                                            }}>
                                                <WalletOutlined style={{
                                                    fontSize: 14,
                                                    color: creditColor,
                                                    animation: creditLoading ? "credit-spin 1s linear infinite" : "none",
                                                }} />
                                                <span style={{ fontSize: 12, fontWeight: 700, color: creditColor, lineHeight: 1 }}>
                                                    ${templateCredit.toFixed(2)}
                                                </span>
                                            </div>
                                        </AntTooltip>
                                    );
                                })()}
                                <ApprovalCenter />
                                <Tooltip
                                    placement="left"
                                    title={isWatiConnected ? "WhatsApp live — connected" : "WhatsApp disconnected — trying to reconnect…"}
                                >
                                    <div style={{ position: "relative", cursor: "default", lineHeight: 0 }}>
                                        <WhatsAppOutlined style={{ fontSize: 24, color: isWatiConnected ? "#52c41a" : "#faad14" }} />
                                        <span style={{
                                            position: "absolute", bottom: -1, right: -3,
                                            width: 9, height: 9, borderRadius: "50%",
                                            background: isWatiConnected ? "#52c41a" : "#faad14",
                                            border: "2px solid #0e1013",
                                            display: "block",
                                        }} />
                                    </div>
                                </Tooltip>
                                {(() => {
                                    const status = watiPresence?.status || null;
                                    const cfg = {
                                        connecting: { color: "#faad14", pulse: true, label: "Connecting to WATI" },
                                        connected: { color: "#52c41a", pulse: false, label: "Online in WATI" },
                                        retrying: { color: "#faad14", pulse: true, label: "Reconnecting to WATI" },
                                        waiting_for_token: { color: "#faad14", pulse: true, label: "Refreshing WATI session" },
                                        failed: { color: "#ff4d4f", pulse: false, label: "WATI connection failed" },
                                        disconnected: { color: "#8c8c8c", pulse: false, label: "Offline in WATI" },
                                    }[status] || { color: "#8c8c8c", pulse: false, label: "WATI presence unknown" };
                                    const tooltipTitle = status === "failed" && watiPresence?.last_error
                                        ? `${cfg.label} — ${watiPresence.last_error}`
                                        : cfg.label;
                                    return (
                                        <AntTooltip placement="left" title={tooltipTitle}>
                                            <div style={{ position: "relative", cursor: "default", lineHeight: 0 }}>
                                                <UserSwitchOutlined style={{ fontSize: 22, color: cfg.color }} />
                                                <span style={{
                                                    position: "absolute", bottom: -1, right: -3,
                                                    width: 9, height: 9, borderRadius: "50%",
                                                    background: cfg.color,
                                                    border: "2px solid #0e1013",
                                                    display: "block",
                                                    animation: cfg.pulse ? "credit-pulse 0.8s ease-in-out infinite" : "none",
                                                }} />
                                            </div>
                                        </AntTooltip>
                                    );
                                })()}
                                <Dropdown
                                    className='cursor-pointer'
                                    menu={{ items: dropdown_items, onClick: onMenuClick }}
                                    placement="bottomLeft"
                                >
                                    <Tooltip placement='left' title={`Logged in as ${userState?.user?.name || "Unknown User"}`}>
                                        <Avatar
                                        size={isMobile ? "default" : "large"}
                                        style={{ backgroundColor: '#fde3cf', color: '#f56a00', marginRight: 8 }}
                                    >
                                        {stringAvatar(userState?.user?.name || "Unknown User")}
                                        </Avatar>
                                    </Tooltip>
                                </Dropdown>
                            </div>
                        </Header>
                    </div>
                    <Content style={isQV4
                        ? { flex: 1, overflow: 'hidden', minHeight: 0, margin: 0 }
                        : { margin: isMobile ? '0 4px' : '0 16px' }
                    }>
                        <div
                            style={isQV4
                                ? { height: '100%', overflow: 'hidden' }
                                : { padding: isMobile ? 8 : 24, minHeight: 360, background: colorBgContainer, borderRadius: borderRadiusLG }
                            }
                        >
                            {/* Inner boundary: page-chunk loads keep the nav shell visible */}
                            <Suspense fallback={
                                <div style={{ height: "100%", minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Spin size="large" />
                                </div>
                            }>
                                <Outlet context={{ setPageDrawerWidth }} />
                            </Suspense>
                        </div>
                    </Content>
                    {!isQV4 && (
                        <Footer style={{ textAlign: 'center', fontSize: isMobile ? 10 : 11, color: "#8c8c8c", padding: isMobile ? "12px 16px" : "14px 16px", background: "transparent" }}>
                            UzdPart Used Spare Parts Trading LLC © {new Date().getFullYear()} · {siteConfig.name} v{siteConfig.version} · build {siteConfig.build_no}
                        </Footer>
                    )}
                </Layout>
            </Layout>
            {LogoutDialog}
            <SettingsModal
                open_settings={globalSettingsOpen}
                handleCloseSettingsModal={() => setGlobalSettingsOpen(false)}
            />
            <ToastContainer />
        </ConfigProvider>
    );
};

export default DrawerNew;